/**
 * A grade de horários do dia.
 *
 * São muitas linhas — uma por hora, ou por meia hora — e pouca altura na
 * coluna. Por isso o motor não decide um número fixo de linhas: ele entrega a
 * lista de horários e o desenho divide a coluna entre elas. Assim a grade das
 * 5h às 23h cabe tanto no A4 quanto no A6, sem nunca cortar a última hora.
 *
 * Quem manda no horário é o **grupo de dias** da página: segunda a sexta,
 * sábado e domingo têm cada um o seu início e o seu fim, e o grupo marcado como
 * livre sai só com linhas de anotação.
 */
import { partesDe } from '../calendar/datas.js';
import { CHAVE_DO_GRUPO_HORARIO, criarGradeHorariosPadrao, } from '../types/planner.js';
/** Minutos desde a meia-noite. `null` quando o texto não é uma hora válida. */
export function minutosDeHora(texto) {
    const achado = /^([01]\d|2[0-3]):([0-5]\d)$/.exec((texto ?? '').trim());
    if (!achado)
        return null;
    return Number(achado[1]) * 60 + Number(achado[2]);
}
/** `420` vira `07:00`. */
export function rotuloDaHora(minutos) {
    const normalizado = Math.min(Math.max(Math.round(minutos), 0), 23 * 60 + 30);
    const hora = String(Math.floor(normalizado / 60)).padStart(2, '0');
    const minuto = String(normalizado % 60).padStart(2, '0');
    return `${hora}:${minuto}`;
}
/**
 * Grupo de dias de uma data.
 *
 * Sem data — agenda permanente, ou uma folha de dia solta — vale segunda a
 * sexta, que é o horário que quase todo mundo espera encontrar.
 */
export function grupoHorarioDaData(dataISO) {
    if (!dataISO)
        return 'SEG_A_SEX';
    const diaSemana = partesDe(dataISO).diaSemana;
    if (diaSemana === 6)
        return 'SABADO';
    if (diaSemana === 0)
        return 'DOMINGO';
    return 'SEG_A_SEX';
}
/**
 * A grade da configuração, com os padrões preenchidos.
 *
 * O campo é novo e opcional: o miolo salvo antes do recurso abre sem ele, e aí
 * vale o horário padrão — mesmo desligado, o quadro não aparece.
 */
export function gradeHorariosDe(config) {
    const padrao = criarGradeHorariosPadrao();
    const salva = config.gradeHorarios;
    const faixa = (bruta, referencia) => {
        const inicio = bruta?.inicio?.trim();
        const fim = bruta?.fim?.trim();
        return {
            inicio: minutosDeHora(inicio) === null ? referencia.inicio : inicio,
            fim: minutosDeHora(fim) === null ? referencia.fim : fim,
            livre: bruta?.livre === true,
        };
    };
    return {
        segAsex: faixa(salva?.segAsex, padrao.segAsex),
        sabado: faixa(salva?.sabado, padrao.sabado),
        domingo: faixa(salva?.domingo, padrao.domingo),
        // Qualquer outro valor cai na hora cheia, que é a grade mais legível.
        intervaloMinutos: salva?.intervaloMinutos === 30 ? 30 : 60,
    };
}
/** A faixa de horário de um grupo de dias. */
export function faixaDoGrupo(config, grupo) {
    return gradeHorariosDe(config)[CHAVE_DO_GRUPO_HORARIO[grupo]];
}
/**
 * Os rótulos da grade: de `inicio` a `fim`, no passo escolhido.
 *
 * A hora final entra sempre, mesmo que a conta não bata. E, se o fim vier antes
 * do início (configuração mexida à mão), a faixa encolhe para o início mais um
 * passo — melhor duas linhas do que uma grade vazia. O rótulo repetido é
 * descartado: depois das 23h não existe hora para acrescentar.
 */
export function horasDaGrade(faixa, intervaloMinutos) {
    const passo = intervaloMinutos === 30 ? 30 : 60;
    const inicio = minutosDeHora(faixa.inicio) ?? 7 * 60;
    const fim = Math.max(minutosDeHora(faixa.fim) ?? 19 * 60, inicio + passo);
    const horas = [];
    for (let minuto = inicio; minuto <= fim; minuto += passo) {
        const rotulo = rotuloDaHora(minuto);
        if (horas[horas.length - 1] !== rotulo)
            horas.push(rotulo);
    }
    return horas;
}
/**
 * A grade de um dia: a lista de horas, ou `null` quando o grupo está livre.
 *
 * `null` é o domingo sem compromisso: quem chama desenha linhas no lugar da
 * grade.
 */
export function horasDoDia(config, dataISO) {
    const grade = gradeHorariosDe(config);
    const faixa = grade[CHAVE_DO_GRUPO_HORARIO[grupoHorarioDaData(dataISO)]];
    if (faixa.livre)
        return null;
    return horasDaGrade(faixa, grade.intervaloMinutos);
}
//# sourceMappingURL=horarios.js.map